import { useModalContext } from '#pplx/frontend/aether/modals/context/useModalContext.js';
import { useEntropyBrowser } from '#pplx/frontend/apps/ask/comet-browser/providers/EntropyBrowserHooks.js';
import { useBrandedVisitorGate } from '#pplx/frontend/apps/ask/hooks/branded-visitor-gate-hooks/useBrandedVisitorGate.js';
import { useHandleClickPricing } from '#pplx/frontend/apps/ask/hooks/pricing-hooks/useHandleClickPricing.js';
import { openWebSubscriptionPage, shouldOpenSubscriptionOnWeb, } from '#pplx/frontend/apps/ask/hooks/subscription-hooks/webSubscriptionRedirect.js';
import { usePaywallModalsOptional } from '#pplx/frontend/apps/ask/paywall-ui/context/PaywallModalsContextSupport.js';
import { creditUpsellCheckoutReturnPath } from '#pplx/frontend/apps/ask/permalink-slug/creditUpsellReturn.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { useIsUserLoggedIn } from '#pplx/frontend/auth/userHooks.js';
import { isCometMobileBrowser } from '#pplx/frontend/comet/client/utils.js';
import { ModalOrigin } from '#pplx/frontend/components/types/types.js';
import * as BUILD_CAPABILITIES from '#pplx/frontend/deployment-capabilities/build.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useCallback, useRef } from 'react';
const ComparisonPaywallModal = Dynamic(async () => {
    const { ComparisonPaywallModal } = await import('#pplx/frontend/apps/ask/hooks/pricing-hooks/ComparisonPaywallModal.js');
    return { default: ComparisonPaywallModal };
});
const UpgradeModal = Dynamic(async () => {
    const { UpgradeModal } = await import('#pplx/frontend/apps/ask/paywall-ui/upgrade/UpgradeModal.js');
    return { default: UpgradeModal };
});
export const usePaywallUpsell = ({ allowLoggedOut = false, enterpriseOnboardingReturnTo, redirectUrl, creditUpsellReturnPath, } = {}) => {
    const isUserLoggedIn = useIsUserLoggedIn();
    const { isPro, isMax, isEnterprise, hasActiveSubscription } = useIsSubscribed();
    const { openModal } = useModalContext();
    const cometAdapter = useEntropyBrowser(false);
    const { handleClickPersonal, handleClickEnterprise } = useHandleClickPricing({
        reason: 'paywall-upsell',
        redirectPath: creditUpsellReturnPath
            ? creditUpsellCheckoutReturnPath(creditUpsellReturnPath)
            : redirectUrl,
        enterpriseOnboardingReturnTo,
        resumeCheckoutAfterLogin: allowLoggedOut,
    });
    const { openVisitorLoginUpsell } = useBrandedVisitorGate({
        enabled: !isUserLoggedIn,
    });
    const paywallModals = usePaywallModalsOptional();
    // Use ref to allow openPaywallUpsell to pass itself to the modal without circular deps
    const openPaywallUpsellRef = useRef(() => { });
    const openPaywallUpsell = useCallback(({ directMaxUpgrade = false, ...props }) => {
        if (!BUILD_CAPABILITIES.payments)
            return;
        const { allowDowngrade = false } = props;
        if (!isUserLoggedIn && !allowLoggedOut) {
            logger.warn('User is not logged in, skipping paywall upsell', props);
            return;
        }
        if (isMax && !allowDowngrade) {
            logger.warn('User is on Max tier, skipping paywall upsell', props);
            return;
        }
        if (shouldOpenSubscriptionOnWeb()) {
            const currentTierPage = isMax ? 'max' : 'pro';
            const upsellTargetPage = isPro ? 'max' : 'pro';
            openWebSubscriptionPage(allowDowngrade ? currentTierPage : upsellTargetPage, 'paywall-upsell');
            return;
        }
        if (!paywallModals) {
            if (import.meta.env.VITE_IS_SIDECAR_BUILD) {
                window.open(`/?upsell=upgrade&src=${ModalOrigin.COMET_SIDECAR_UPGRADE}`, '_blank', 'noopener,noreferrer');
                return;
            }
            logger.warn('PaywallModalsContext not available, skipping paywall upsell');
            return;
        }
        // For mobile Comet (iOS/Android), open native upgrade sheet
        if (isCometMobileBrowser()) {
            if (!cometAdapter) {
                logger.error('Comet adapter not available in mobile Comet browser context');
                return;
            }
            cometAdapter.openNativeSheet('upgrade');
            return;
        }
        if (directMaxUpgrade && isPro && hasActiveSubscription && !isEnterprise) {
            openModal(UpgradeModal, {
                onClose: props.onClose,
                creditUpsellReturnPath,
                IncentiveProModal: paywallModals.IncentiveProModal,
                BraintreeSubscriptionModal: paywallModals.BraintreeSubscriptionModal,
                openPaywallUpsell: openPaywallUpsellRef.current,
            }, { replace: false });
            return;
        }
        const highlightTier = isPro ? 'max' : undefined;
        // For web, open the pricing modal with pricing callbacks
        // Pass openPaywallUpsell via ref so the modal can re-open itself (e.g., after student verification)
        openModal(ComparisonPaywallModal, {
            creditUpsellReturnPath,
            highlightTier,
            ...props,
            handleClickPersonal,
            handleClickEnterprise,
            openPaywallUpsell: openPaywallUpsellRef.current,
            openVisitorLoginUpsell,
            IncentiveProModal: paywallModals.IncentiveProModal,
            BraintreeSubscriptionModal: paywallModals.BraintreeSubscriptionModal,
        }, { replace: false });
    }, [
        creditUpsellReturnPath,
        allowLoggedOut,
        isPro,
        isEnterprise,
        hasActiveSubscription,
        isUserLoggedIn,
        openModal,
        isMax,
        cometAdapter,
        handleClickPersonal,
        handleClickEnterprise,
        openVisitorLoginUpsell,
        paywallModals,
    ]);
    // Keep ref in sync with the latest callback
    openPaywallUpsellRef.current = openPaywallUpsell;
    return openPaywallUpsell;
};
//# sourceMappingURL=usePaywallUpsell.js.map